import { supabase } from '../utils/supabase';

/**
 * Block Booking Service
 * Handles CRUD operations for session blocks (recurring bookings)
 */

/**
 * Create a block of recurring sessions
 * @param {Object} blockData - Block booking data
 * @param {string} blockData.clubId - Club ID
 * @param {string} blockData.blockName - Name for the block
 * @param {string} blockData.description - Optional description
 * @param {string} blockData.recurrencePattern - Pattern: 'weekly', 'bi-weekly', 'custom'
 * @param {number} blockData.recurrenceInterval - Days between occurrences
 * @param {Array} blockData.sessions - Array of session data objects
 * @param {string} blockData.createdBy - Player ID of creator
 * @returns {Promise<Object>} Created block and sessions
 */
export const createSessionBlock = async (blockData) => {
  const {
    clubId,
    blockName,
    description,
    recurrencePattern,
    recurrenceInterval,
    sessions,
    createdBy
  } = blockData;

  try {
    // 1. Create session_block record
    const { data: block, error: blockError } = await supabase
      .from('session_blocks')
      .insert({
        club_id: clubId,
        block_name: blockName,
        description,
        recurrence_pattern: recurrencePattern,
        recurrence_interval: recurrenceInterval,
        total_sessions: sessions.length,
        created_by: createdBy
      })
      .select()
      .single();

    if (blockError) {
      console.error('Error creating session block:', blockError);
      throw blockError;
    }

    // 2. Add session_block_id to all session objects
    const sessionsToCreate = sessions.map(session => ({
      ...session,
      session_block_id: block.id,
      club_id: clubId,
      created_by: createdBy,
      session_status: 'draft',
    }));

    // 3. Create all sessions with session_block_id
    const { data: createdSessions, error: sessionsError } = await supabase
      .from('sessions')
      .insert(sessionsToCreate)
      .select();

    if (sessionsError) {
      console.error('Error creating sessions:', sessionsError);
      // Rollback: delete the block if sessions failed
      await supabase.from('session_blocks').delete().eq('id', block.id);
      throw sessionsError;
    }

    return {
      success: true,
      block,
      sessions: createdSessions
    };
  } catch (error) {
    console.error('Error in createSessionBlock:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get all session blocks for a club
 * @param {string} clubId - Club ID
 * @returns {Promise<Array>} Array of session blocks with their sessions
 */
export const getSessionBlocks = async (clubId) => {
  try {
    const { data, error } = await supabase
      .from('session_blocks')
      .select(`
        *,
        sessions (
          id,
          session_date,
          start_time,
          end_time,
          name,
          session_status,
          max_players,
          current_players,
          session_series_id,
          session_series:session_series_id (
            id,
            name,
            is_active
          )
        )
      `)
      .eq('club_id', clubId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching session blocks:', error);
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Error in getSessionBlocks:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Get a single session block by ID
 * @param {string} blockId - Session block ID
 * @returns {Promise<Object>} Session block with sessions
 */
export const getSessionBlock = async (blockId) => {
  try {
    const { data, error } = await supabase
      .from('session_blocks')
      .select(`
        *,
        sessions (
          *,
          session_types:session_type_id (
            id,
            name,
            description
          ),
          session_series:session_series_id (
            id,
            name,
            is_active
          ),
          session_games ( id )
        )
      `)
      .eq('id', blockId)
      .single();

    if (error) {
      console.error('Error fetching session block:', error);
      throw error;
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error in getSessionBlock:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update a session block's metadata
 * @param {string} blockId - Session block ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>} Updated block
 */
export const updateSessionBlock = async (blockId, updates) => {
  try {
    const { data, error } = await supabase
      .from('session_blocks')
      .update(updates)
      .eq('id', blockId)
      .select()
      .single();

    if (error) {
      console.error('Error updating session block:', error);
      throw error;
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error in updateSessionBlock:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update future upcoming/draft sessions in a block.
 * Skips past dates and live, completed, archived, or cancelled sessions.
 * @param {string} blockId - Session block ID
 * @param {Object} sessionUpdates - Updates to apply to sessions
 * @returns {Promise<Object>} Result
 */
export const updateBlockSessions = async (blockId, sessionUpdates) => {
  try {
    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('sessions')
      .update(sessionUpdates)
      .eq('session_block_id', blockId)
      .gte('session_date', today)
      .in('session_status', ['upcoming', 'draft'])
      .select();

    if (error) {
      console.error('Error updating block sessions:', error);
      throw error;
    }

    return {
      success: true,
      data,
      updatedCount: data?.length || 0
    };
  } catch (error) {
    console.error('Error in updateBlockSessions:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

const STARTED_OR_FINISHED_STATUSES = ['ongoing', 'completed', 'archived'];

/**
 * Unplayed block sessions that may be removed when deleting a block.
 * Keeps live, completed, or archived sessions, and any session that already has games.
 * @param {Object} session
 * @returns {boolean}
 */
export const isBlockSessionDeletable = (session) => {
  if (!session) return false;
  const started = STARTED_OR_FINISHED_STATUSES.includes(session.session_status);
  const hasGames = (session.session_games?.length ?? 0) > 0;
  return !started && !hasGames;
};

/**
 * Delete unplayed sessions in a block, unlink started/completed sessions, then remove the block.
 * @param {string} blockId
 * @returns {Promise<Object>} Result with deletedCount and keptCount
 */
export const deleteUnplayedBlockSessions = async (blockId) => {
  if (!blockId) {
    return { success: false, error: 'Block ID is required' };
  }

  try {
    const blockResult = await getSessionBlock(blockId);
    if (!blockResult.success || !blockResult.data) {
      throw new Error(blockResult.error || 'Failed to load block');
    }

    const sessions = blockResult.data.sessions || [];
    const deletable = sessions.filter(isBlockSessionDeletable);
    const kept = sessions.filter((session) => !isBlockSessionDeletable(session));

    for (const session of deletable) {
      const { error: deleteError } = await supabase.rpc('delete_session_with_dependencies', {
        p_session_id: session.id,
      });
      if (deleteError) {
        console.error('Error deleting block session:', deleteError);
        throw deleteError;
      }
    }

    if (kept.length > 0) {
      const { error: unlinkError } = await supabase
        .from('sessions')
        .update({ session_block_id: null })
        .eq('session_block_id', blockId);

      if (unlinkError) {
        console.error('Error unlinking kept block sessions:', unlinkError);
        throw unlinkError;
      }
    }

    const { error: blockError } = await supabase
      .from('session_blocks')
      .delete()
      .eq('id', blockId);

    if (blockError) {
      console.error('Error deleting block:', blockError);
      throw blockError;
    }

    return {
      success: true,
      deletedCount: deletable.length,
      keptCount: kept.length,
    };
  } catch (error) {
    console.error('Error in deleteUnplayedBlockSessions:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Delete a session block
 * @param {string} blockId - Session block ID
 * @param {string} deleteOption - 'block_only', 'all_sessions', 'future_sessions'
 * @returns {Promise<Object>} Result
 */
export const deleteSessionBlock = async (blockId, deleteOption = 'block_only') => {
  try {
    const today = new Date().toISOString().split('T')[0];

    if (deleteOption === 'all_sessions') {
      // Delete all sessions in the block
      const { error: sessionsError } = await supabase
        .from('sessions')
        .delete()
        .eq('session_block_id', blockId);

      if (sessionsError) {
        console.error('Error deleting sessions:', sessionsError);
        throw sessionsError;
      }
    } else if (deleteOption === 'future_sessions') {
      // Delete only future sessions
      const { error: sessionsError } = await supabase
        .from('sessions')
        .delete()
        .eq('session_block_id', blockId)
        .gte('session_date', today);

      if (sessionsError) {
        console.error('Error deleting future sessions:', sessionsError);
        throw sessionsError;
      }

      // Remove block_id from past sessions
      await supabase
        .from('sessions')
        .update({ session_block_id: null })
        .eq('session_block_id', blockId)
        .lt('session_date', today);
    } else {
      // Just remove block linkage from all sessions
      const { error: unlinkError } = await supabase
        .from('sessions')
        .update({ session_block_id: null })
        .eq('session_block_id', blockId);

      if (unlinkError) {
        console.error('Error unlinking sessions:', unlinkError);
        throw unlinkError;
      }
    }

    // Delete the block record
    const { error: blockError } = await supabase
      .from('session_blocks')
      .delete()
      .eq('id', blockId);

    if (blockError) {
      console.error('Error deleting block:', blockError);
      throw blockError;
    }

    return {
      success: true,
      message: 'Session block deleted successfully'
    };
  } catch (error) {
    console.error('Error in deleteSessionBlock:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Deactivate a session block (soft delete)
 * @param {string} blockId - Session block ID
 * @returns {Promise<Object>} Result
 */
export const deactivateSessionBlock = async (blockId) => {
  try {
    const { data, error } = await supabase
      .from('session_blocks')
      .update({ is_active: false })
      .eq('id', blockId)
      .select()
      .single();

    if (error) {
      console.error('Error deactivating session block:', error);
      throw error;
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error in deactivateSessionBlock:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get sessions count by block ID
 * @param {string} blockId - Session block ID
 * @returns {Promise<Object>} Session counts by status
 */
export const getBlockSessionCounts = async (blockId) => {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('session_status')
      .eq('session_block_id', blockId);

    if (error) {
      console.error('Error fetching block session counts:', error);
      throw error;
    }

    const counts = {
      total: data.length,
      draft: 0,
      upcoming: 0,
      ongoing: 0,
      completed: 0,
      cancelled: 0,
      archived: 0
    };

    data.forEach(session => {
      if (counts[session.session_status] !== undefined) {
        counts[session.session_status]++;
      }
    });

    return {
      success: true,
      counts
    };
  } catch (error) {
    console.error('Error in getBlockSessionCounts:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
